import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { DatePicker, Select, Button, Table, Input } from 'antd'

import Layout from '../../components/layout/Layout'
import axiosInstance from '../../utils/axiosInstance'
import { mainGrowthAreaArray, growthAreasObject, engagementTypeArray, leadershipSkillArray } from '../../constants/focusAreaItems'

const { RangePicker, Avatar } = DatePicker

const timeZones = [
  {
    value: '-5',
    label: '-5',
  },
  {
    value: '-6',
    label: '-6',
  },
  {
    value: '+5',
    label: '+5',
  },
  {
    value: '+6',
    label: '+6',
  },
]

const columns = [
  {
    title: 'First Name',
    key: 'firstName',
    render: (data) => <div>{data.createdBy.firstName}</div>,
    sorter: (a, b) => a.createdBy.firstName.localeCompare(b.createdBy.firstName),
  },
  {
    title: 'Last Name',
    key: 'lastName',
    render: (data) => <div>{data.createdBy.lastName}</div>,
    sorter: (a, b) => a.createdBy.lastName.localeCompare(b.createdBy.lastName),
  },
  {
    title: 'Date',
    key: 'dateTime',
    sorter: (a, b) => a.dateTime - b.dateTime,
    render: (data) => <div>{moment(data?.dateTime).format('MM/DD')}</div>,
  },
  {
    title: 'Activity',
    key: 'theEventItem',
    sorter: (a, b) => {
      const subTitleA = a.theEventItem?.subTitle || ''
      const subTitleB = b.theEventItem?.subTitle || ''
      return subTitleA.localeCompare(subTitleB)
    },
    render: (data) => {
      const subTitle = data.theEventItem?.subTitle
      return <div>{subTitle || 'N/A'}</div>
    },
  },
  {
    title: 'Points',
    key: 'points',
    render: (data) => <div>{data.theEventItem?.points || 0}</div>, // Default to 0
    sorter: (a, b) => (a.theEventItem?.points || 0) - (b.theEventItem?.points || 0),
  },
  {
    title: 'Category',
    key: 'category',
    render: (data) => <div>{data.theEventItem?.category || 'N/A'}</div>,
    sorter: (a, b) => {
      const categoryA = a.theEventItem?.category || ''
      const categoryB = b.theEventItem?.category || ''
      return categoryA.localeCompare(categoryB)
    },
  },
  {
    title: 'Area',
    key: 'focusArea',
    render: (data) => <div>{data.parent?.title || 'N/A'}</div>,
    sorter: (a, b) => {
      const titleA = a.parent?.title || ''
      const titleB = b.parent?.title || ''
      return titleA.localeCompare(titleB)
    },
  },
  {
    title: 'Attachments',
    key: 'attachments',
    render: (data) => (
      <div>
        {data.uploadedResponses.map((el) => (
          <a href={el} target="_blank">
            Download{' '}
          </a>
        ))}
      </div>
    ),
    sorter: (a, b) => {
      const aHasAttachments = a.uploadedResponses && a.uploadedResponses.length > 0
      const bHasAttachments = b.uploadedResponses && b.uploadedResponses.length > 0

      if (aHasAttachments && !bHasAttachments) {
        return -1 // a comes before b
      }
      if (!aHasAttachments && bHasAttachments) {
        return 1 // b comes before a
      }
      return 0 // Maintain original order if both have or both don't have attachments
    },
  },
  {
    title: 'Location',
    key: 'location',
    render: (data) => <div>{data?.location?.address?.[0]?.city}</div>,
    sorter: (a, b) => {
      const cityA = a?.location?.address?.[0]?.city || ''
      const cityB = b?.location?.address?.[0]?.city || ''
      return cityA.localeCompare(cityB)
    },
  },
  {
    title: 'Text',
    dataIndex: 'proveItText',
    key: 'proveItText',
    sorter: (a, b) => {
      const contentA = a?.proveItText || ''
      const contentB = b?.proveItText || ''
      return contentA.localeCompare(contentB)
    },
  },
  {
    title: 'Data menu',
    dataIndex: 'dynamicText',
    key: 'dynamicText',
    sorter: (a, b) => {
      const contentA = a?.dynamicText || ''
      const contentB = b?.dynamicText || ''
      return contentA.localeCompare(contentB)
    },
  },
  {
    title: 'Tagged members',
    key: 'taggedMembers',
    render: (data) => (
      <div>
        {data.taggedFriendsDetails.map((el) => (
          <>{`${el.firstName} ${el.lastName} ▪️`}</>
        ))}
      </div>
    ),
    sorter: (a, b) => {
      const aHasTags = a.taggedFriendsDetails && a.taggedFriendsDetails.length > 0
      const bHasTags = b.taggedFriendsDetails && b.taggedFriendsDetails.length > 0

      if (aHasTags && !bHasTags) {
        return -1 // a comes before b
      }
      if (!aHasTags && bHasTags) {
        return 1 // b comes before a
      }
      return 0 // Maintain original order if both have or both don't have taggedMembers
    },
  },
  {
    title: 'Growth Area',
    key: 'growthArea',
    render: (data) => {
      // Safely access growthArea; if it's missing, 'null' will be passed to find
      const growthAreaValue = data.theEventItem?.growthArea
      const label = mainGrowthAreaArray.find((elem) => elem.value === growthAreaValue)?.label
      return <div>{label || 'N/A'}</div>
    },
    sorter: (a, b) => {
      const getLabel = (item) => {
        const growthAreaValue = item?.theEventItem?.growthArea
        return mainGrowthAreaArray.find((elem) => elem.value === growthAreaValue)?.label || ''
      }

      const labelA = getLabel(a)
      const labelB = getLabel(b)

      return labelA.localeCompare(labelB)
    },
  },
  {
    title: 'Specific Growth Shift',
    key: 'specificGrowth',
    render: (data) => {
      const growthAreaValue = data.theEventItem?.growthArea
      const specificGrowthValue = data.theEventItem?.specificGrowth

      const label = growthAreaValue && growthAreasObject[growthAreaValue]?.find((elem) => elem.id == specificGrowthValue)?.label

      return <div>{label || 'N/A'}</div>
    },
    sorter: (a, b) => {
      const getLabel = (item) => {
        const growthAreaValue = item.theEventItem?.growthArea
        const specificGrowthValue = item.theEventItem?.specificGrowth

        return (growthAreaValue && growthAreasObject[growthAreaValue]?.find((elem) => elem.id == specificGrowthValue)?.label) || ''
      }

      const labelA = getLabel(a)
      const labelB = getLabel(b)

      return labelA.localeCompare(labelB)
    },
  },
  {
    title: 'Leader Skill',
    key: 'leaderSkill',
    render: (data) => <div>{leadershipSkillArray.find((elem) => elem.value == data.theEventItem?.leaderSkill)?.label || 'N/A'}</div>,
    sorter: (a, b) => {
      const getLabel = (value) => leadershipSkillArray.find((elem) => elem.value == value)?.label || ''

      const labelA = getLabel(a.theEventItem?.leaderSkill)
      const labelB = getLabel(b.theEventItem?.leaderSkill)

      return labelA.localeCompare(labelB)
    },
  },
  {
    title: 'Engagement Type',
    key: 'engagementType',
    render: (data) => <div>{engagementTypeArray.find((elem) => elem.value == data.theEventItem?.engagementType)?.label || 'N/A'}</div>,
    sorter: (a, b) => {
      const getLabel = (value) => engagementTypeArray.find((elem) => elem.value == value)?.label || ''

      const labelA = getLabel(a.theEventItem?.engagementType)
      const labelB = getLabel(b.theEventItem?.engagementType)

      return labelA.localeCompare(labelB)
    },
  },
]

function ProveIt() {
  const [loading, setLoading] = useState(false)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [organizations, setOrganizations] = useState([])
  const [selectedOrg, setSelectedOrg] = useState('')
  const [timeZone, setTimeZone] = useState(timeZones[0].value)
  const [hourBlock, setHourBlock] = useState('')
  const [category, setCategory] = useState('')
  const [selectedFocusArea, setSelectedFocusArea] = useState('')
  const [activities, setActivities] = useState([])
  const [selectedActivity, setSelectedActivity] = useState('') // id of the Focus Item
  const [data, setData] = useState(null)

  const getMyInfo = async () => {
    try {
      setLoading(true)

      const response = await axiosInstance.get(`/api/v1/admin/me`)

      // Check the admin organizations
      if (response?.data?.user?.organizations && response?.data?.user?.organizations.length > 0) {
        const org = response.data.user.organizations
        // console.log(org)

        // store to render at JSX
        org.forEach((element, idx) => {
          if (idx == 0) setSelectedOrg(element._id) // auto selecting the first organization to fetch some results
          setOrganizations((prevElements) => [
            ...prevElements,
            {
              value: element._id,
              label: element.name,
            },
          ])
        })
      }

      setLoading(false)
    } catch (e) {
      console.log(e)
      setLoading(false)
    }
  }

  const getData = async () => {
    try {
      if (!startDate || !endDate) {
        console.log(startDate, endDate)
        return
      }

      setLoading(true)

      const payload = {
        timeZone,
        startDate,
        endDate,
        summaryOfClub: selectedOrg,
        ...(selectedActivity ? { selectedActivity } : {}), // e.g. subtitle
      }

      // console.log(payload)

      // ? Just to fast forward the testings use this portin if needed
      // localStorage.setItem('temp1', JSON.stringify(payload))
      // let payloadCache = localStorage.getItem('temp1')
      // payloadCache = JSON.parse(payloadCache)

      const response = await axiosInstance.post(`/api/v1/metrics/proveItPage`, payload)

      // console.log(response.data.tasks)
      setData(response.data?.tasks)

      setLoading(false)
    } catch (e) {
      console.log(e)
      setLoading(false)
    }
  }

  const handleRangePicker = (value) => {
    // console.log('Selected Time: ', value)
    value?.map((el, idx) => {
      // console.log(moment(new Date(el)).format('MMMM DD, YYYY h:mm:ss a')) // Readable format
      // console.log(new Date(el).getTime()) // Epoch timestamp in miliseconds
      let start = 0
      let end = 0
      if (idx == 0) {
        start = new Date(el).getTime()
        setStartDate(start)
      } else if (idx == 1) {
        end = new Date(el).getTime()
        setEndDate(end)
      }
      console.log(start)
      console.log(end)
    })
  }

  const handleSearch = async (val) => {
    if (!val.trim()) {
      return
    }

    try {
      setLoading(true)
      const response = await axiosInstance.post('/api/v1/search/globalSearch', {
        query: val,
      })
      console.log('bye')
      console.log('Success', `Results: ${JSON.stringify(response.data)}`)
      // setActivities(response.data.searchResults)
      const items = response.data.searchResults.map((elem) => {
        return {
          label: `(${elem.category}) ${elem.subTitle} `,
          value: elem._id,
        }
      })
      console.log(items)
      setActivities(items)
    } catch (error) {
      console.error(error)
    }
    setLoading(false)
  }

  useEffect(() => {
    getMyInfo()
  }, [])

  if (organizations.length == 0) return null

  return (
    <Layout>
      <h1>Prove it</h1>
      <RangePicker onChange={handleRangePicker} />
      <Select
        placeholder="Please select club or business"
        style={{ width: 250 }}
        defaultValue={organizations[0].value}
        onChange={(value) => setSelectedOrg(value)}
        options={organizations}
      />
      <Select placeholder="Please select timezone" style={{ width: 100 }} defaultValue={timeZones[0].value} onChange={(value) => setTimeZone(value)} options={timeZones} />

      {/* <Select style={{ width: 300 }} showSearch filterOption={false} onSearch={handleSearch} options={activities} onChange={(value) => setSelectedActivity(value)} /> */}

      <Button type="primary" style={{ cursor: 'pointer' }} onClick={getData} loading={loading}>
        Get data
      </Button>
      <div style={{ marginTop: 10 }}></div>
      <Table columns={columns} dataSource={data} pagination={false} scroll={{ x: 300 }} />
    </Layout>
  )
}

export default ProveIt
